import { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createBookingSuccess,
  setBookingRef,
  setPublicReference,
} from '../../../store/reducers/bookingReducer';
import {
  getPublicReferenceSelector,
} from '../../../store/selectors/bookingSelectors';
import { BookingDetailsResponseDto, BookingResponseDto } from '../../../api-client/generated';
import {
  handleCheckDraftBooking,
  handleCheckDraftBookingResponse,
  saveDraftBooking,
  getDraftBooking,
  removeDraftBooking,
  BookingDraft,
  getCompleteBooking
} from '../services/DraftBookingService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const WARNING_TIMES = [15, 10, 5]; // Minutes before expiry to show warnings
const EXPIRY_CHECK_INTERVAL =  60 * 1000; // 1 minute

export const useBookingDraft = (
  booking: BookingResponseDto | BookingDetailsResponseDto | undefined,
  isInPaymentPhase: boolean
) => {
  const dispatch = useDispatch();
  const publicReference = useSelector(getPublicReferenceSelector);
  const [validationChecked, setValidationChecked] = useState(false);
  const { t } = useTranslation(['bookings']);
  const warningsShown = useRef<Set<number>>(new Set());

  // Restore publicReference synchronously before browser paint
  useLayoutEffect(() => {
    if (!publicReference) {
      const draft = getDraftBooking();
      if (draft?.publicReference) {
        dispatch(setPublicReference({ publicReference: draft.publicReference }));
      }
    }
  }, [dispatch, publicReference]);

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Clears both localStorage draft and Redux booking state
   */
  const clearDraftAndBooking = useCallback(() => {
    removeDraftBooking();
    dispatch(createBookingSuccess({ booking: undefined as any }));
  }, [dispatch]);
  /**
   * Validates beach chair availability and filters out unavailable chairs
   */
  const getInValidBeachChairs = useCallback(
    async (draft: BookingDraft) => {
      const beachChairAvailable: handleCheckDraftBookingResponse[] = await handleCheckDraftBooking(
        draft.booking,
        draft.publicReference || publicReference
      );

      const validNumbers = beachChairAvailable
        .filter(chair => chair.isAvailable)
        .map(chair => chair.beachChairNumber);

      const draftChairs = draft.booking.beachChairBookings || [];
      const validChairs: any[] = [];

      for (let i = 0; i < draftChairs.length; i++) {
        const chair = draftChairs[i];
        if (!validNumbers.includes((chair as any).publicNumber)) {
          validChairs.push(chair);
        }
      }

      return validChairs;
    },
    [publicReference]
  );

  /**
   * Updates booking in Redux store and optionally sets booking reference
   */
  const updateBooking = useCallback(
    (cleanedBooking: BookingResponseDto, draftPublicRef?: string) => {
      dispatch(createBookingSuccess({ booking: cleanedBooking }));

      if (draftPublicRef) {
        dispatch(setBookingRef({
          bookingRef: cleanedBooking.bookingRef || '',
          publicReference: draftPublicRef
        }));
        dispatch(setPublicReference({ publicReference: draftPublicRef }));
      }
    },
    [dispatch]
  );

  /**
   * Saves current booking to localStorage as draft
   */
  const handleSaveDraft = useCallback(async () => {
    if (!booking) return;
    const hasBeachChairs = booking.beachChairBookings?.length > 0;
    const hasExtras = booking.extras?.length > 0;
    if (!hasBeachChairs && !hasExtras) return;

    // Preserve email from existing draft if available
    const existingDraft = getDraftBooking();
    const email = existingDraft?.email;

    saveDraftBooking(booking, publicReference, email);
  }, [booking, publicReference]);

  // ==================== DRAFT VALIDATION & RESTORATION ====================

  /**
   * Validates and restores draft booking on mount
   * Handles expiry, complete bookings, and chair availability
   */
  // ==================== DRAFT VALIDATION HELPERS ====================

  /**
   * Checks if draft has expired based on timestamp
   */
  const isDraftExpired = useCallback((draft: BookingDraft): boolean => {
    return draft.expires ? Date.now() > draft.expires : false;
  }, []);

  /**
   * Checks if booking was already completed in localStorage
   */
  const isBookingCompletedLocally = useCallback((draft: BookingDraft): boolean => {
    const completeBooking = getCompleteBooking();
    return !!(completeBooking && completeBooking.bookingRef === draft.booking.bookingRef);
  }, []);

  /**
   * Checks if booking is confirmed on the server (handles missed redirects)
   */
  const isBookingConfirmedOnServer = useCallback(async (draft: BookingDraft): Promise<boolean> => {
    if (!draft.booking.bookingRef || !draft.email) {
      return false;
    }

    try {
      const { publicApi } = await import('../../../api/api');
      const { data: bookingDetails } = await publicApi.publicControllerPublicBookingDetails({
        email: draft.email,
        bookingRef: draft.booking.bookingRef,
      });

      return bookingDetails.status === 'CONFIRMED';
    } catch (error) {
      console.warn('Failed to check booking status on server:', error);
      return false;
    }
  }, []);

  /**
   * Validates chair availability and updates booking if needed
   */
  const validateAndCleanChairs = useCallback(async (draft: BookingDraft) => {
    const invalidChairs = await getInValidBeachChairs(draft);

    if (invalidChairs.length > 0) {
      const cleanedBooking = {
        ...draft.booking,
        beachChairBookings: invalidChairs
      };
      saveDraftBooking(cleanedBooking, draft.publicReference);
      updateBooking(cleanedBooking as BookingResponseDto, draft.publicReference);
    } else {
      // No valid chairs remaining - remove draft
      removeDraftBooking();
    }
  }, [getInValidBeachChairs, updateBooking]);

  // ==================== DRAFT VALIDATION & RESTORATION ====================

  useEffect(() => {
    const validateAndRestoreDraft = async () => {
      if (validationChecked) return;

      const draft = getDraftBooking();

      // No draft found - nothing to restore
      if (!draft) {
        setValidationChecked(true);
        return;
      }

      // Draft expired - clear it
      if (isDraftExpired(draft)) {
        clearDraftAndBooking();
        setValidationChecked(true);
        return;
      }

      // Draft has no booking data - invalid
      if (!draft.booking) {
        setValidationChecked(true);
        return;
      }

      // Booking already completed locally - clear draft
      if (isBookingCompletedLocally(draft)) {
        clearDraftAndBooking();
        setValidationChecked(true);
        return;
      }

      // Booking confirmed on server - clear draft
      if (await isBookingConfirmedOnServer(draft)) {
        clearDraftAndBooking();
        setValidationChecked(true);
        return;
      }

      // Validate chair availability and restore draft
      await validateAndCleanChairs(draft);
      setValidationChecked(true);
    };

    validateAndRestoreDraft();
  }, [validationChecked, clearDraftAndBooking, isBookingCompletedLocally, isBookingConfirmedOnServer, isDraftExpired, validateAndCleanChairs]);

  // ==================== CART SYNCHRONIZATION ====================

  /**
   * Syncs cart when browser tab becomes visible
   * Validates chair availability and updates if needed
   */
  const syncCartOnVisibilityChange = useCallback(
    async () => {
      const draft = getDraftBooking();
      if (!draft?.booking) return;

      const draftChairs = draft.booking.beachChairBookings || [];
      const currentChairs = booking?.beachChairBookings || [];

      // Only sync if lengths differ
      if (draftChairs.length === currentChairs.length) return;

      const inValidChairs = await getInValidBeachChairs(draft);

      if (inValidChairs.length > 0) {
        const syncedBooking = {
          ...draft.booking,
          beachChairBookings: inValidChairs,
          extras: booking?.extras || draft.booking.extras,
        };

        updateBooking(syncedBooking as BookingResponseDto, draft.publicReference);
        saveDraftBooking(syncedBooking, draft.publicReference);
        toast.success(t('bookings:cartSyncedSuccessfully'));
      }
    },
    [booking, getInValidBeachChairs, updateBooking, t]
  );

  // ==================== BROWSER EVENT HANDLERS ====================

  /**
   * Handles visibility changes and page unload
   * Saves draft when hidden, syncs when visible, prompts before leaving with unsaved changes
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        await handleSaveDraft();
      } else if (document.visibilityState === 'visible') {
        await syncCartOnVisibilityChange();
      }
    };

    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      await handleSaveDraft();

      // Prompt user if they have unsaved items and aren't in payment
      if ((booking?.beachChairBookings?.length || booking?.extras?.length) && !isInPaymentPhase) {
        e.preventDefault();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [booking, isInPaymentPhase, handleSaveDraft, syncCartOnVisibilityChange]);

  // ==================== DRAFT EXPIRY MONITORING ====================

  /**
   * Continuously monitors draft expiry and shows warnings
   * Clears draft when expired
   */
  useEffect(() => {
    const shownWarnings = warningsShown.current;

    const checkExpiry = () => {
      const draft = getDraftBooking();
      if (!draft?.expires) return;

      const timeRemaining = draft.expires - Date.now();
      const minutesRemaining = Math.floor(timeRemaining / (60 * 1000));

      // Draft has expired - clear it
      if (timeRemaining <= 0) {
        clearDraftAndBooking();
        toast.warning(t('bookings:draftExpiryWarning', { minutes: 0 }));
        return;
      }

      // Show warnings at specific time intervals
      WARNING_TIMES.forEach(warningMinutes => {
        if (minutesRemaining === warningMinutes && !shownWarnings.has(warningMinutes)) {
          shownWarnings.add(warningMinutes);
          toast.warning(t('bookings:draftExpiryWarning', { minutes: warningMinutes }));
        }
      });
    };

    const expiryCheckInterval = setInterval(checkExpiry, EXPIRY_CHECK_INTERVAL);
    checkExpiry(); // Check immediately on mount

    return () => {
      clearInterval(expiryCheckInterval);
      shownWarnings.clear();
    };
  }, [dispatch, t, clearDraftAndBooking]);
};
